import {
    FileAttachment,
    FileAttachmentStatusEnum,
    FileAttachmentTypeEnum,
    ImageUploadErrType,
    ReduxState,
    Response,
    TextDirection,
} from 'interface';
import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { toast } from 'react-toastify';
import { upload } from 'scripts/api';
import { __CameraProps, __CameraRef, __CaptureType } from './camera.interface';
import './camera.style.scss';
import { Spinner } from 'react-bootstrap';
import HintText from '../hintText/hintText.index';
import QuestionImage from '../questionImage/questionImage.index';
import { API } from 'data';
import { UploadImgErrToast } from 'data/errorCodeToast';
import { FileManager } from 'scripts/fileManager';
import Compressor from 'compressorjs';
import { extractTitle } from 'scripts/string';
import { MessageBridge } from 'scripts/messageBridge';
import ENV from 'env.json';
export { __CaptureType as SLPCaptureType };
export interface SLPCameraRef extends __CameraRef {}

const SLPCamera: React.ForwardRefRenderFunction<__CameraRef, ConnectedProps<typeof connector> & __CameraProps> =
    function (props: ConnectedProps<typeof connector> & __CameraProps, ref) {
        const lang = props.lang.utilities.SLPCamera;
        const [isUploading, setUploading] = useState(false);
        const videoRef = useRef<HTMLVideoElement>(null);
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const [error, setError] = useState<string>();
        const [file, setFile] = useState<FileAttachment | undefined>(props.defaultValue);
        const immediateValue = useRef<FileAttachment | undefined>(file);
        const labelRef = useRef<HTMLLabelElement>(null);
        const [streamStarted, setStreamStart] = useState(false);
        const [showWebcam, setShowWebcam] = useState(false);
        const [isDone, setDone] = useState<File>();
        const [twoCams, isTowCams] = useState(false);

        React.useImperativeHandle(ref, () => ({
            getValue: () => immediateValue.current,
            setError: (text) => {
                setError(text);
            },
        }));

        useEffect(() => {
            if (streamStarted) {
                videoRef.current?.play();
                setShowWebcam(true);
            }
        }, [streamStarted]);

        useEffect(() => {
            if (streamStarted) {
                videoRef.current?.play();
                setShowWebcam(true);
            }
        }, [streamStarted]);

        useEffect(() => {
            if (streamStarted) isDone ? videoRef.current?.pause() : videoRef.current?.play();
        }, [isDone]);

        useEffect(() => {
            if (labelRef.current && props.label) labelRef.current.innerHTML = extractTitle(props.label, false);
        }, [props.label]);

        function file2Attachment(e: File, id: string): FileAttachment {
            const titles = e.name.split('.');
            const ext = titles[1];
            return {
                file: id,
                id: id,
                created_at: new Date().toISOString(),
                workspace: props.ws ?? 0,
                extension: ext,
                name: e.name,
                filename: e.name,
                offset: 0,
                status: FileAttachmentStatusEnum.complete,
                type: FileAttachmentTypeEnum.image,
                size: e.size / 1024 / 1024,
            };
        }

        function onCoverUpload() {
            if (props.onDrop) props.onDrop();
            const FILE = createFile(true);
            if (FILE) {
                setShowWebcam(false);
                // can upload file
                setUploading(true);

                new Compressor(FILE.file, {
                    convertSize: 400000,
                    maxWidth: 1980,
                    maxHeight: 1980,
                    success(result) {
                        const target = new File([result], FILE.file.name, {});
                        const form = new FormData();
                        form.append('file', target);
                        form.append('alt', target.name);
                        form.append('filename', target.name);
                        form.append('name', target.name);
                        form.append('type', FileAttachmentTypeEnum.image.toString());
                        form.append('workspace', props.ws.toString());
                        upload<FileAttachment>(API.upload.slp(props.accessToken ?? ''), form, false)
                            .then((res) => {
                                const file = res.data;
                                immediateValue.current = file;
                                setFile(immediateValue.current);
                                if (props.onChange) props.onChange(file);
                                setError(undefined);
                            })
                            .catch((res: Response<ImageUploadErrType>) => {
                                if (res.status >= 500) toast.error(props.lang.serverErr);
                                else if (res.status >= 400) UploadImgErrToast(res.data);
                                else {
                                    // offline mode
                                    const id = FileManager.StoreFile(FILE.file);
                                    const newFile = file2Attachment(target, id);
                                    immediateValue.current = newFile;
                                    setFile(immediateValue.current);
                                    if (props.onChange) props.onChange(newFile);
                                    setError(undefined);
                                }
                            })
                            .finally(() => setUploading(false));
                    },
                    error(err) {
                        console.log(err.message);
                    },
                });
            }
        }

        const createFile = (setFormat = false) => {
            const canvas = canvasRef.current;
            const video = videoRef.current;
            if (canvas && video) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas.getContext('2d')?.drawImage(video, 0, 0);
                const dataurl = canvas.toDataURL('image/webp');
                const arr = dataurl.split(',');
                const mime = arr[0].match(/:(.*?);/)?.[1],
                    bstr = atob(arr[1]);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) u8arr[n] = bstr.charCodeAt(n);
                return {
                    file: new File(
                        [u8arr],
                        `${props.capture ? props.capture : 'user'}-${new Date().toDateString()}${
                            setFormat ? '.webp' : ''
                        }`,
                        {
                            type: mime,
                        },
                    ),
                    url: dataurl,
                };
            }
        };

        function onClickCamera() {
            const internalProcess = () => {
                if ('mediaDevices' in navigator && navigator.mediaDevices.getUserMedia) {
                    navigator.mediaDevices.enumerateDevices().then((devices) => {
                        const videoDevices = devices.filter((device) => device.kind === 'videoinput');
                        isTowCams(videoDevices.length > 1);
                        navigator.mediaDevices
                            .getUserMedia({
                                video: {
                                    width: window.innerWidth,
                                    height: window.innerHeight,
                                    facingMode: videoDevices.length > 1 ? { exact: props.capture } : undefined,
                                },
                            })
                            .then((stream) => {
                                videoRef.current?.pause();
                                if (videoRef.current) videoRef.current.srcObject = stream;
                                setStreamStart(true);
                            });
                    });
                }
            };
            if (streamStarted) setShowWebcam(true);
            else {
                if (ENV.mode !== 'production') {
                    internalProcess();
                } else {
                    MessageBridge.request('ASK_PERMISSION_FROM_USER', {
                        key: 'CAMERA',
                        title: 'درخواست دسترسی به دوربین',
                        message: 'ما برای دریافت پاسخ شما به دسترسی دوربین احتیاج داریم.',
                        // buttonNeutral: string,
                        buttonPositive: 'تایید',
                        // buttonNegative: 'عدم تایید',
                    }).then((res: 'GRANTED' | 'DENIED' | 'NEVER_ASK_AGAIN') => {
                        if (res !== 'GRANTED') {
                            toast.error('برای عکس برداری نیاز به اجازه دسترسی به دوربین است');
                        } else {
                            internalProcess();
                        }
                    });
                }
            }
        }

        return (
            <div className={`ucheck-SLP-camera-image-attachment ${props.dir === TextDirection.rtl ? 'rtl-text' : ''}`}>
                <span className="d-flex">
                    {props.required && <span className="required-star">*</span>}
                    <span className="counter-span">{props.counter ? props.counter + '- ' : ''}</span>
                    <label ref={labelRef} style={{ color: props.labelColor }} />
                </span>
                <QuestionImage src={props.image?.file} />
                <HintText value={props.hint} />
                <div
                    className={`video-container ${
                        twoCams && props.capture !== __CaptureType.user ? 'environment-view' : ''
                    } ${showWebcam ? '' : 'd-none'}`}
                >
                    <video ref={videoRef}></video>
                    <div>
                        <i
                            onClick={isDone ? onCoverUpload : () => setDone(createFile()?.file)}
                            className={`material-icons ${isDone ? 'reverse-icon' : ''}`}
                        >
                            {isDone ? 'done' : 'camera_alt'}
                        </i>
                        <i
                            onClick={() => (isDone ? setDone(undefined) : setShowWebcam(false))}
                            className="material-icons"
                        >
                            clear
                        </i>
                    </div>
                </div>
                <canvas ref={canvasRef} className="d-none"></canvas>
                <div
                    className="camera-input"
                    onClick={onClickCamera}
                    style={{
                        border: `${props.color ? props.color + ' dashed 1px ' : ''}`,
                        backgroundColor: `${
                            props.color ? (props.color.length > 4 ? props.color + '30' : props.color + '3') : ''
                        }`,
                    }}
                >
                    {isUploading ? (
                        <Spinner animation="border" size="sm" variant="dark" />
                    ) : file ? (
                        <span>{lang.uploaded}</span>
                    ) : (
                        <div className="blue-box" style={{ backgroundColor: `${props.color ? props.color : ''}` }}>
                            {lang.capture}
                        </div>
                    )}
                </div>
                {error && <p className="input-error">{error}</p>}
            </div>
        );
    };

const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    isAuth: state.authStatus,
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPCamera));
